import { CACHE_NAMESPACE } from "./types";
import { PredictionCache } from "./PredictionCache";

/**
 * Lightweight localStorage-backed cache with TTL enforcement.
 * Falls back gracefully if storage is unavailable.
 */
export class LocalPredictionCache extends PredictionCache {
  constructor(ttlMs = 60 * 60 * 1000) {
    super();
    this.ttlMs = ttlMs;
    this.storage = this.safeStorage();
  }

  safeStorage() {
    try {
      const testKey = '__cache_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch (err) {
      console.warn('[LocalPredictionCache] localStorage unavailable, cache disabled', err);
      return null;
    }
  }

  makeKey({ userId, sport, gameId }) {
    return `${CACHE_NAMESPACE}:${userId || 'anon'}:${sport}:${gameId}`;
  }

  now() {
    return Date.now();
  }

  async getMany(keys) {
    if (!this.storage) return {};
    const now = this.now();
    const result = {};

    keys.forEach((keyObj) => {
      const storageKey = this.makeKey(keyObj);
      const raw = this.storage.getItem(storageKey);
      if (!raw) return;
      try {
        const parsed = JSON.parse(raw);
        if (parsed.expiresAt && parsed.expiresAt > now && parsed.prediction) {
          result[String(keyObj.gameId)] = parsed.prediction;
        } else {
          // Expired; clean up
          this.storage.removeItem(storageKey);
        }
      } catch (err) {
        console.warn('[LocalPredictionCache] parse error, purging key', storageKey, err);
        this.storage.removeItem(storageKey);
      }
    });

    return result;
  }

  async putMany(entries) {
    if (!this.storage) return;
    entries.forEach((entry) => {
      const storageKey = this.makeKey(entry);
      const payload = {
        prediction: entry.prediction,
        expiresAt: entry.expiresAt || this.now() + this.ttlMs
      };
      try {
        this.storage.setItem(storageKey, JSON.stringify(payload));
      } catch (err) {
        console.warn('[LocalPredictionCache] setItem failed', err);
      }
    });
  }

  async removeMany(keys) {
    if (!this.storage) return;
    keys.forEach((keyObj) => {
      const storageKey = this.makeKey(keyObj);
      this.storage.removeItem(storageKey);
    });
  }
}
